/* eslint-disable no-nested-ternary */
import { CiqIconBox } from "components/svg-icons";
import { Button } from "antd";
import { useState } from "react";
import { ErrorMessages } from "constants/index";
import Tooltip from "antd/es/tooltip";
import { FeatureTypes } from "entity-app/constants";
import { useLocation } from "react-router";
import { InfoCircleOutlined } from "@ant-design/icons";
import {
  TEditingEntity,
  TEntityLink,
  TFeatureDetail,
  TLinkSource
} from "../models";
import FeatureRowCard from "./cards";
import ActivityTrackingCheckbox from "./track-chechbox/activity-tracking-chechbox";
import MaterialToMaterialTrackingCheckbox from "./track-chechbox/material-material-tracking";

type EntityProps = {
  list: TLinkSource[];
  featureDetail: TFeatureDetail;
  isEditing: boolean;
  isDisabled: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => Promise<void>;
  editingEntity: TEditingEntity | null;
  setEditingEntity: React.Dispatch<React.SetStateAction<TEditingEntity | null>>;
  refetchEntityInfo: () => Promise<void>;
  selectedEntityInfo: TEntityLink;
  featureTypeId: FeatureTypes;
};

function LinkedFeatureItems({
  list,
  featureDetail,
  isEditing,
  isDisabled,
  onEdit,
  onCancel,
  onSave,
  editingEntity,
  setEditingEntity,
  refetchEntityInfo,
  selectedEntityInfo,
  featureTypeId
}: EntityProps) {
  const [loading, setLoading] = useState(false);

  const isActivityCheckboxAvailable =
    selectedEntityInfo.feature_type_id === FeatureTypes.SUBMITTALS &&
    featureDetail.featureTypeId === FeatureTypes.ACTIVITIES;

  const isMaterialCheckboxAvailable =
    selectedEntityInfo.feature_type_id === FeatureTypes.MATERIALS &&
    featureDetail.featureTypeId === FeatureTypes.MATERIALS;

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const source = urlParams.get("source");

  const [isActivityTrackingChecked, setActivityTrackingChecked] = useState(
    !!(
      list.length > 0 ||
      !isActivityCheckboxAvailable ||
      (source && source === "submittal")
    )
  );

  const [isMaterialTrackingChecked, setMaterialTrackingChecked] = useState(
    !!(
      list.length > 0 ||
      !isMaterialCheckboxAvailable ||
      (source && source === "material")
    )
  );

  const isCollapsed =
    isDisabled || !isActivityTrackingChecked || !isMaterialTrackingChecked;
  const isExpanded = isActivityTrackingChecked && isMaterialTrackingChecked;

  return (
    <div
      style={{
        ...(isCollapsed && { minHeight: 56, maxHeight: 56 }),
        ...(isExpanded && !isEditing && { minHeight: 0 })
      }}
      className={`flex flex-col relative w-full transition-all duration-300 px-5 border-0 border-b border-solid border-ciq-gray-800 border-opacity-20  ${
        isEditing ? "grow h-full" : isExpanded ? "grow" : ""
      }`}
    >
      <div className="flex w-full justify-between px-1.5 py-2.5">
        <h4
          data-testid="linked-feature-items-title"
          className="flex items-center space-x-1 font-semibold"
        >
          <CiqIconBox feature={featureDetail.feature} />
          <div className="pl-1">Associated {featureDetail.titlePlural}</div>
          <span data-testid="linked-feature-items-count" className="">
            {list.length > 0 ? ` (${list.length})` : ""}
          </span>
          {isMaterialCheckboxAvailable && (
            <Tooltip title="Materials listed below are Successors of this Material, unless mentioned.">
              <InfoCircleOutlined
                data-testid="material-info-tooltip"
                className="pl-[3px] text-ciq-gray-700"
              />
            </Tooltip>
          )}
        </h4>
        <div className="flex items-center space-x-1">
          {!isEditing && !isDisabled && isActivityCheckboxAvailable && (
            <ActivityTrackingCheckbox
              list={list}
              refetchEntityInfo={refetchEntityInfo}
              submittalIds={selectedEntityInfo.ids}
              checked={isActivityTrackingChecked}
              setChecked={setActivityTrackingChecked}
            />
          )}

          {!isEditing && !isDisabled && isMaterialCheckboxAvailable && (
            <MaterialToMaterialTrackingCheckbox
              list={list}
              refetchEntityInfo={refetchEntityInfo}
              selectedEntityInfo={selectedEntityInfo}
              checked={isMaterialTrackingChecked}
              setChecked={setMaterialTrackingChecked}
            />
          )}

          {isEditing ? (
            <div className="space-x-2">
              <Button
                onClick={onCancel}
                disabled={loading || !isExpanded}
                data-testid="linkedfeatureitems-button-cancel"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                data-testid="linkedfeatureitems-button-save"
                onClick={async () => {
                  setLoading(true);
                  await onSave();
                  setLoading(false);
                }}
                loading={loading || !isExpanded}
              >
                Save
              </Button>
            </div>
          ) : (
            <Tooltip
              title={isDisabled ? ErrorMessages.EditingLinkingMessage : null}
            >
              {isMaterialCheckboxAvailable && !isMaterialTrackingChecked ? (
                ""
              ) : (
                <Button
                  type="primary"
                  onClick={onEdit}
                  data-testid="linkedfeatureitems-button-add-edit"
                  disabled={isDisabled || isCollapsed}
                >
                  {list.length ? "Edit" : "Add"}
                </Button>
              )}
            </Tooltip>
          )}
        </div>
      </div>
      <div
        data-testid="linked-feature-items-content"
        className={`w-full transition-all duration-500 ${
          isCollapsed ? "hidden" : "overflow-auto grow"
        }`}
      >
        {list.length ? (
          list.map((item) => (
            <FeatureRowCard
              item={item}
              isEditing={isEditing}
              editingEntity={editingEntity}
              setEditingEntity={setEditingEntity}
              featureTypeId={featureTypeId}
              isAllowSetting={
                selectedEntityInfo?.feature_type_id === FeatureTypes.MATERIALS
              }
              selectedEntityInfo={selectedEntityInfo}
            />
          ))
        ) : (
          <div
            data-testid="linked-feature-items-empty-state"
            className="flex h-full w-full justify-center items-center text-center pb-10"
          >
            <div data-testid="linked-feature-items-empty-message">
              No {featureDetail.titlePlural} linked
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LinkedFeatureItems;
